<template>
  <ElDialog
    class="mail-service-page-add-modal"
    :visible="value"
    width="830px"
    @close="closeAndReset"
  >
    <div slot="title" class="mail-service-page-add-modal__header">
      {{ $t('ADVERTISEMENT.MAIL_SERVICE.ADD_NOTIFICATION_TITLE') }}
      <p class="mail-service-page-add-modal__header-desc">
        {{ $t('ADVERTISEMENT.MAIL_SERVICE.NIGHTLY_NOTIFICATIONS_DESCRIPTION') }}
      </p>
    </div>
    <ElForm
      :ref="formRef"
      label-position="left"
      class="mail-service-page-add-modal__form"
      :model="formModel"
      :rules="formRules"
      @submit.native.prevent="handleAddEmail"
    >
      <ElAlert
        v-if="showSelectAtLeastOneNotificationError"
        class="mail-service-page-add-modal__notification-min-error"
        :closable="false"
        type="error"
        show-icon
      >
        {{ $t('ADVERTISEMENT.MAIL_SERVICE.NOTIFICATION_MIN_ERROR') }}
      </ElAlert>

      <ElFormItem
        label-width="12.5rem"
        class="mail-service-page-add-modal__form-item mail-service-page-add-modal__form-item--separated"
        :label="$t('ADVERTISEMENT.MAIL_SERVICE.GENERAL_NOTIFICATIONS')"
      >
        <ElCheckbox
          v-model="formModel.monthlyNotification"
          :label="mailNotificationTypes.NEWSLETTER"
        >
          {{ $t('ADVERTISEMENT.MAIL_SERVICE.GENERAL_MONTHLY_NOTIFICATION') }}
        </ElCheckbox>
      </ElFormItem>
      <ElFormItem
        label-width="12.5rem"
        class="mail-service-page-add-modal__form-item mail-service-page-add-modal__form-item--separated"
        :label="$t('ADVERTISEMENT.MAIL_SERVICE.FINANCIAL_NOTIFICATIONS')"
      >
        <ElCheckboxGroup v-model="formModel.financialNotifications">
          <ElCheckbox :label="mailNotificationTypes.LOW_BUDGET">
            <i18n
              path="ADVERTISEMENT.MAIL_SERVICE.FINANCIAL_END_BUDGET_NOTIFICATION"
            >
              <ElInput
                v-model.number="formModel.lowBudgetThreshold"
                size="mini"
                class="mail-service-page-add-modal__end-budget-threshold"
                type="number"
                dir="ltr"
              />
            </i18n>
          </ElCheckbox>
          <ElCheckbox :label="mailNotificationTypes.DAILY_STATS">
            {{
              $t('ADVERTISEMENT.MAIL_SERVICE.FINANCIAL_NIGHTLY_NOTIFICATION')
            }}
          </ElCheckbox>
        </ElCheckboxGroup>
      </ElFormItem>
      <ElFormItem
        label-width="12.5rem"
        class="mail-service-page-add-modal__form-item mail-service-page-add-modal__form-item--separated"
        :label="
          $t('ADVERTISEMENT.MAIL_SERVICE.CAMPAIGN_MANAGEMENT_NOTIFICATIONS')
        "
      >
        <ElCheckboxGroup v-model="formModel.campaignRelatedNotifications">
          <ElCheckbox :label="mailNotificationTypes.CAMPAIGNS_STOP_ALERTS">
            {{ $t('ADVERTISEMENT.MAIL_SERVICE.CAMPAIGN_STOP_ALERTS') }}
          </ElCheckbox>
          <ElCheckbox :label="mailNotificationTypes.KEYWORD_ALERTS">
            {{ $t('ADVERTISEMENT.MAIL_SERVICE.CAMPAIGN_KEYWORD_RELATED') }}
          </ElCheckbox>
        </ElCheckboxGroup>
      </ElFormItem>
      <ElFormItem
        label-width="28.75rem"
        class="mail-service-page-add-modal__form-item mail-service-page-add-modal__form-item--email"
        required
        prop="email"
        :label="$t('ADVERTISEMENT.MAIL_SERVICE.TO_BE_SENT_EMAIL_TITLE')"
      >
        <ElInput v-model="formModel.email" type="email" />
      </ElFormItem>
    </ElForm>
    <div slot="footer" class="mail-service-page-add-modal__footer">
      <ElButton
        type="primary"
        native-type="submit"
        size="large"
        :loading="isLoading"
        @click="handleAddEmail"
      >
        {{ $t('SAVE') }}
      </ElButton>
      <ElButton type="text" size="large" @click="closeAndReset">
        {{ $t('CANCEL') }}
      </ElButton>
    </div>
  </ElDialog>
</template>

<script>
import form from '@/mixins/form';
import { ADD_MAIL_SERVICE } from '@/store/types';
import { mapActions } from 'vuex';
import mailNotificationTypes from '../constants/mailNotificationTypes';
import transformToServerNotificationTypes from '../transformers/transformToServerNotificationTypes';
import required from '@/utils/validation-rules/required';
import emailValidation from '@/utils/validation-rules/email';
import { AD_MAIL_SERVICE_ADD_MODAL_BUTTON } from '@/constants/gtm-events';

export default {
  name: 'MailServicePageAddModal',
  mixins: [form],
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      formModel: {
        monthlyNotification: true,
        financialNotifications: [
          mailNotificationTypes.LOW_BUDGET,
          mailNotificationTypes.DAILY_STATS,
        ],
        campaignRelatedNotifications: [
          mailNotificationTypes.CAMPAIGNS_STOP_ALERTS,
          mailNotificationTypes.KEYWORD_ALERTS,
        ],
        lowBudgetThreshold: 3_000_000,
        email: '',
      },
      isLoading: false,
      showSelectAtLeastOneNotificationError: false,
    };
  },
  computed: {
    mailNotificationTypes() {
      return mailNotificationTypes;
    },
    formRules() {
      return {
        email: [
          required(this.$t('VALIDATION_REQUIRED_ERR')),
          emailValidation(),
        ],
      };
    },
    formRef() {
      return 'addMailForm';
    },
    areAnyNotificationSelected() {
      return (
        this.formModel.monthlyNotification ||
        this.formModel.financialNotifications.length ||
        this.formModel.campaignRelatedNotifications.length
      );
    },
  },
  methods: {
    ...mapActions({
      addEmail: ADD_MAIL_SERVICE,
    }),
    validateNotificationMinSelection() {
      if (!this.areAnyNotificationSelected) {
        this.showSelectAtLeastOneNotificationError = true;
        return false;
      }
      this.showSelectAtLeastOneNotificationError = false;
      return true;
    },
    closeAndReset() {
      this.resetForm(this.formRef);

      this.$emit('input', false);
    },
    handleAddEmail() {
      if (
        !this.isValid(this.formRef) ||
        !this.validateNotificationMinSelection()
      )
        return;

      this.$gtm.trackEvent(AD_MAIL_SERVICE_ADD_MODAL_BUTTON);

      this.isLoading = true;

      const transformedNotificationTypes = transformToServerNotificationTypes({
        monthlyNotification: this.formModel.monthlyNotification,
        financialNotifications: this.formModel.financialNotifications,
        campaignRelatedNotifications: this.formModel
          .campaignRelatedNotifications,
      });

      return this.addEmail({
        ...transformedNotificationTypes,
        email: this.formModel.email,
        lowBudgetThreshold: this.formModel.lowBudgetThreshold,
      })
        .then(message => {
          this.$message.success(message);

          this.closeAndReset();
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.mail-service-page-add-modal {
  &__notification-min-error {
    margin-bottom: $global-margin * 2;
  }

  &__header {
    font-size: $--font-size-large;

    &-desc {
      margin-top: $global-margin / 2;
      font-size: $lb-small-font-size;
      color: $lb-dark-gray;
    }
  }

  &__end-budget-threshold {
    width: rem-calc(100px);

    /* Firefox */
    input[type='number'] {
      /* stylelint-disable-next-line */
      -moz-appearance: textfield;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      /* stylelint-disable-next-line */
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__form {
    &-item {
      .el-form-item__content {
        @include rtl() {
          margin-left: 0 !important;
          // 12.5rem which is declared inside template
          margin-right: 12.5rem;
        }
      }

      .el-form-item__label {
        height: auto;
        line-height: initial;
      }

      .el-checkbox-group {
        display: flex;
        flex-direction: column;
      }

      .el-checkbox {
        margin-left: 0 !important;
        margin-right: 0 !important;

        &:not(:first-child) {
          margin-top: $global-margin;
        }

        &__label {
          vertical-align: middle;
          white-space: normal !important;
        }
      }

      &--separated {
        border-bottom: 1px solid $lb-light-gray;
        padding-bottom: $global-padding * 1.5;
      }

      &--email {
        border: 1px solid $lb-light-gray;
        padding: $global-padding * 1.5;
        border-radius: $lb-border-radius * 2.5;

        .el-form-item__content {
          /* stylelint-disable-next-line */
          @include rtl() {
            margin-left: 0 !important;
            // 28.75rem which is declared inside template
            margin-right: rem-calc(460px);
          }
        }
      }
    }
  }

  &__footer {
    display: flex;
    flex-direction: row-reverse;

    .el-button {
      &:not(:first-child) {
        margin-right: $global-margin;
        margin-left: 0;
        @include rtl() {
          margin-right: 0;
          margin-left: $global-margin;
        }
      }
    }
  }
}
</style>
